import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  Add,
  Delete,
  EyeInvisible,
  EyeVisible,
  LayersFront,
  Plus,
} from '@factorialco/f0-react/icons/app'
import {
  FormsCustomTemplatesDocument,
  FormsCustomTemplatesFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useDraftTemplate,
  useDuplicateTemplate,
  usePublishTemplate,
} from 'modules/forms/hooks/useFormTemplates'

import DeleteFormTemplate from './DeleteFormTemplate'
import {
  type ColumnConfigurations,
  type TableColumnType,
  createBaseColumns,
  createBaseColumnsList,
} from './columnsHandler'

interface Props {
  resourceType: string
  columns: TableColumnType[]
  configurations?: ColumnConfigurations
  createRoute?: string
  itemOnClick?: (item: FormsCustomTemplatesFragment) => void
  onlyRead?: boolean
  onlyActive?: boolean
  mode?: 'table' | 'list' | 'all'
}
const FormsTemplatesDataCollection = ({
  resourceType,
  columns,
  configurations,
  createRoute,
  itemOnClick,
  onlyRead,
  onlyActive,
  mode = 'table',
}: Props) => {
  const navigate = useNavigate()
  const [templateIdToDelete, setTemplateIdToDelete] = useState<string | undefined>(undefined)

  const tableColumns = useMemo(() => {
    return createBaseColumns(columns, configurations)
  }, [columns, configurations])

  const { publishFormTemplate } = usePublishTemplate()

  const onPublishTemplate = async (id: string) => {
    const result = await publishFormTemplate({ id, resourceType })
    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  const { draftFormTemplate } = useDraftTemplate()
  const onDraftTemplate = async (id: string) => {
    const result = await draftFormTemplate({ id, resourceType })
    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  const { duplicateFormTemplate } = useDuplicateTemplate()
  const onDuplicateTemplate = async (
    formId: string,
    resourceType: string,
    duplicateName: string | undefined,
    duplicateDescription: string | undefined
  ) => {
    const result = await duplicateFormTemplate({
      formId,
      resourceType,
      duplicateName,
      duplicateDescription,
    })
    const handled = handleResult(result, () =>
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    )

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  const source = useGraphqlDataCollectionSource({
    document: FormsCustomTemplatesDocument,
    mapData: (data) => data.forms.formsConnection.nodes,
    search: {
      enabled: true,
      sync: true,
    },
    mapVariables: ({ search }) => {
      const payload: {
        onlyActive?: boolean
        resourceType: string
        search?: string
        onlyTemplates: boolean
        sortOrder?: {
          field: string
          order: string
        }
        formType?: string[]
      } = {
        resourceType: resourceType,
        search,
        onlyTemplates: true,
        onlyActive: onlyActive,
      }

      return payload
    },
    mapPagination: (data) => ({
      total: data.forms.formsConnection.totalCount ?? 0,
    }),
    itemOnClick: itemOnClick
      ? (template: FormsCustomTemplatesFragment) => () => itemOnClick(template)
      : undefined,
    primaryActions: onlyRead
      ? undefined
      : () => ({
          label: i18n.t('forms.custom_templates.data_collection.new_template'),
          icon: Add,
          onClick: () => {
            if (createRoute) {
              navigate(createRoute)
            }
          },
        }),

    itemActions: (template: FormsCustomTemplatesFragment) =>
      onlyRead
        ? []
        : [
            template.status == 'draft'
              ? {
                  label: i18n.t('forms.custom_templates.data_collection.actions.publish'),
                  icon: EyeVisible,
                  onClick: () => onPublishTemplate(template.id),
                }
              : {
                  label: i18n.t('forms.custom_templates.data_collection.actions.draft'),
                  icon: EyeInvisible,
                  onClick: () => onDraftTemplate(template.id),
                },
            {
              label: i18n.t('forms.custom_templates.data_collection.actions.duplicate'),
              icon: LayersFront,
              onClick: () =>
                onDuplicateTemplate(
                  template.id,
                  template.resourceType,
                  template.title,
                  template.description ?? undefined
                ),
            },
            {
              label: i18n.t('forms.custom_templates.data_collection.actions.delete'),
              icon: Delete,
              critical: true,
              onClick: () => setTemplateIdToDelete(template.id),
            },
          ],
  })

  const listFields = useMemo(() => {
    return createBaseColumnsList(columns, configurations)
  }, [columns, configurations])

  const visualizations = useMemo(() => {
    switch (mode) {
      case 'list':
        return [
          {
            type: 'list' as const,
            options: {
              fields: listFields.fields,
              itemDefinition: listFields.itemDefinition,
            },
          },
        ]
      case 'table':
        return [
          {
            type: 'table' as const,
            options: {
              columns: tableColumns,
              frozenColumns: 1 as const,
            },
          },
        ]
      default:
      case 'all':
        return [
          {
            type: 'table' as const,
            options: {
              columns: tableColumns,
              frozenColumns: 1 as const,
            },
          },
          {
            type: 'list' as const,
            options: {
              fields: listFields.fields,
              itemDefinition: listFields.itemDefinition,
            },
          },
        ]
    }
  }, [mode, listFields, tableColumns])
  return (
    <>
      <OneDataCollection
        source={source}
        visualizations={visualizations}
        emptyStates={{
          'no-data': {
            title: i18n.t('forms.custom_templates.empty_state.title'),
            description: i18n.t('forms.custom_templates.empty_state.description'),
            actions: [
              {
                label: i18n.t('forms.custom_templates.data_collection.new_template'),
                icon: Plus,
                onClick: () => {
                  if (createRoute) {
                    navigate(createRoute)
                  }
                },
              },
            ],
          },
        }}
      />
      {templateIdToDelete && (
        <DeleteFormTemplate
          templateId={templateIdToDelete}
          onClose={() => setTemplateIdToDelete(undefined)}
        />
      )}
    </>
  )
}

export default FormsTemplatesDataCollection
